import { z } from 'zod'
import { HistoryTables } from '../../../shared'
import {
  AnimalStatusHistoryResponseSchema,
  MappedAnimalRemovalReasonSchema,
} from '../../laravel-data-exporter'
import { DogActivityLevel, DogBodyType, DogEatingHabits, DogGender, DogLifestage } from '../../laravel-data-exporter/types'
import { ActorTypes } from '../../user-event/types'
import { DogBreedSizeClassification, WeightMilestoneSchema } from './puppy-recommendation.schemas'

export const ActivityLevelNameMap: Record<DogActivityLevel, string> = {
  [DogActivityLevel.LOW]: 'Chilled',
  [DogActivityLevel.MEDIUM]: 'Loves to Play',
  [DogActivityLevel.HIGH]: 'Ball of Energy',
}

export const EatingHabitsNameMap: Record<DogEatingHabits, string> = {
  [DogEatingHabits.EATS_ANYTHING]: 'Eats Anything',
  [DogEatingHabits.CHOOSY]: 'Choosy',
  [DogEatingHabits.VERY_FUSSY]: 'Very Fussy',
}

export const DogBreedDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  adultWeightMaleMin: z.number(),
  adultWeightMaleMax: z.number(),
  maleSizeClassification: z.string(),
  adultWeightFemaleMin: z.number(),
  adultWeightFemaleMax: z.number(),
  femaleSizeClassification: z.string(),
  adultAgeMonthsMax: z.number(),
  breedExamples: z.string().nullable(),
  legacyId: z.number().nullish(),
})

export type DogBreedDto = z.infer<typeof DogBreedDtoSchema>

export const DogHealthIssueDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  slug: z.string(),
  legacyId: z.number().nullish(),
})

export type DogHealthIssueDto = z.infer<typeof DogHealthIssueDtoSchema>

export const DogFoodAllergenDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  legacyId: z.number().nullish(),
})

export type DogFoodAllergenDto = z.infer<typeof DogFoodAllergenDtoSchema>

export const DogFoodTypeDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  legacySlug: z.string().nullish(),
})

export type DogFoodTypeDto = z.infer<typeof DogFoodTypeDtoSchema>

export const DogDeactivationChildReasonDtoSchema = z.object({
  id: z.string().uuid(),
  description: z.string(),
  displayOrder: z.number(),
  slug: z.string(),
  laravelRemoveReasonId: z.number().nullish(),
})

export const DogDeactivationReasonDtoSchema = DogDeactivationChildReasonDtoSchema.extend({
  childReasons: DogDeactivationChildReasonDtoSchema.array(),
})

export type DogDeactivationReasonDto = z.infer<typeof DogDeactivationReasonDtoSchema>

const DogGenderSchema = z.nativeEnum(DogGender)
const DogBodyTypeSchema = z.nativeEnum(DogBodyType)
const DogActivityLevelSchema = z.nativeEnum(DogActivityLevel)
const DogEatingHabitsSchema = z.nativeEnum(DogEatingHabits)
const DogLifestageSchema = z.nativeEnum(DogLifestage)
// slug for backend, name for frontend display
const DogActivityLevelDetailsSchema = z.object({
  slug: DogActivityLevelSchema,
  name: z.nativeEnum(ActivityLevelNameMap),
})
const DogEatingHabitsDetailsSchema = z.object({
  slug: DogEatingHabitsSchema,
  name: z.nativeEnum(EatingHabitsNameMap),
})

/**
 * Slim schemas for embedded details - contain only name and slug
 * Used for payload size optimization in *Details fields
 */
export const SlimBreedDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
})
export type SlimBreedDto = z.infer<typeof SlimBreedDtoSchema>

export const SlimHealthIssueDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
})
export type SlimHealthIssueDto = z.infer<typeof SlimHealthIssueDtoSchema>

export const SlimFoodAllergenDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
})
export type SlimFoodAllergenDto = z.infer<typeof SlimFoodAllergenDtoSchema>

export const SlimFoodTypeDtoSchema = z.object({
  name: z.string(),
  slug: z.string(),
})
export type SlimFoodTypeDto = z.infer<typeof SlimFoodTypeDtoSchema>

export const PuppyMealWeightsSchema = z.object({
  adultMealWeightV1: z.number().nonnegative(),
  mealWeightV1: z.number().nonnegative(),
  mealWeightV2: z.number().nonnegative(),
  breedAdultMealWeight: z.number().nonnegative(),
})
export type PuppyMealWeights = z.infer<typeof PuppyMealWeightsSchema>

export const DogDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  dateOfBirth: z.string().date().nullable(),
  dateOfBirthIsApproximate: z.boolean(),
  gender: DogGenderSchema.nullable(),
  dateOfDeath: z.string().date().nullable(),
  dateOfDeathIsApproximate: z.boolean(),
  breeds: z.string().array(),
  recommendedMinWeight: z.number().nullable(),
  recommendedMaxWeight: z.number().nullable(),
  preferredBreedName: z.string().nullable(),
  height: z.number().nullable(),
  weight: z.number().nullable(),
  bodyType: DogBodyTypeSchema.nullable(),
  activityLevel: DogActivityLevelSchema.nullable(),
  eatingHabits: DogEatingHabitsSchema.nullable(),
  healthIssues: z.string().array(),
  foodAllergies: z.string().array(),
  previousFoods: z.string().array(),
  previousFoodBrands: z.string().array(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  dailyEnergyKilocalories: z.number().nullable(), // This is actually adultMealWeight
  // Defined only when lifestage === PUPPY (and required puppy data is present); null otherwise.
  puppyMealWeights: PuppyMealWeightsSchema.nullish(),
  images: z.string().array(),
  currentImageUrl: z.string().nullable(),
  deactivatedAt: z.string().datetime().nullish(),
  primaryDeactivationReason: DogDeactivationReasonDtoSchema.omit({ displayOrder: true, childReasons: true }).nullish(),
  secondaryDeactivationReason: DogDeactivationChildReasonDtoSchema.omit({ displayOrder: true }).nullish(),
  otherDeactivationReason: z.string().nullish(),
  lifestage: DogLifestageSchema.nullish(),
  graduatedAt: z.string().datetime().nullish(),
  ageInMonths: z.number().nullish(),
  // Computed from breed(s) + gender; null when there are no breeds, more than 2 breeds, or breed size cannot be resolved.
  averageAdultAgeMonths: z.number().int().positive().nullable(),
  // Keep both legacy fields (e.g. breeds, activityLevel, eatingHabits, foodAllergies, healthIssues) and the *Details variants for backward compatibility between CA and CCA schemas
  // Note: *Details fields use slim schemas with only name and slug for payload size optimization
  breedsDetails: SlimBreedDtoSchema.array().nullish(),
  activityLevelDetails: DogActivityLevelDetailsSchema.nullish(),
  eatingHabitsDetails: DogEatingHabitsDetailsSchema.nullish(),
  foodAllergiesDetails: SlimFoodAllergenDtoSchema.array().nullish(),
  healthIssuesDetails: SlimHealthIssueDtoSchema.array().nullish(),
  previousFoodsDetails: SlimFoodTypeDtoSchema.array().nullish(),
})
export type DogDto = z.infer<typeof DogDtoSchema>

export const CreateDogBodySchema = z.object({
  name: z.string().nullish(),
  dateOfBirth: z.string().date().nullish(),
  dateOfBirthIsApproximate: z.boolean().nullish(), // This input should be optional, it eventually then be default to false at service level also at db level already
  // Manually assign if this dog is puppy or adult, even it has old age, heavy weight but if we not marking it as ADULT, it is still a PUPPY regardless
  lifestage: DogLifestageSchema.nullish(),
  gender: DogGenderSchema.nullish(),
  dateOfDeath: z.string().date().nullish(),
  dateOfDeathIsApproximate: z.boolean().nullish(), // Same with dateOfBirthIsApproximate
  breeds: z.string().array(),
  height: z.number().nullish(),
  weight: z.number().nullish(),
  bodyType: DogBodyTypeSchema.nullish(),
  activityLevel: DogActivityLevelSchema.nullish(),
  eatingHabits: DogEatingHabitsSchema.nullish(),
  healthIssues: z.string().array(),
  foodAllergies: z.string().array(),
  previousFoods: z.string().array(),
  previousFoodBrands: z.string().array(),
})

export type CreateDogBody = z.infer<typeof CreateDogBodySchema>

export const UpdateDogBodySchema = CreateDogBodySchema.partial()
export type UpdateDogBody = z.infer<typeof UpdateDogBodySchema>

export const DeactivateDogBodySchema = z.object({
  primaryReasonId: z.string().uuid(),
  secondaryReasonId: z.string().uuid().nullish(),
  otherReason: z.string().nullish(),
  actorType: z.enum(ActorTypes).optional().default('system'),
})
export type DeactivateDogBody = z.infer<typeof DeactivateDogBodySchema>

export const GetDogRecommendationBodySchema = z.object({
  gender: DogGenderSchema,
  bodyType: DogBodyTypeSchema,
  activityLevel: DogActivityLevelSchema,
  weight: z.number().gt(0),
  ageWeeks: z.number().nonnegative().optional(),
  breeds: z.string().array().max(2).optional(),
  // When set, forces the recommendation to follow the given lifestage instead of inferring from ageWeeks/breeds.
  // PUPPY: requires ageWeeks and breeds, returns puppy meals & milestones even past adult age threshold.
  // ADULT: returns only dailyEnergyKilocalories; puppy fields & averageAdultAgeMonths are nulled.
  overriddenLifestage: DogLifestageSchema.optional(),
})
export type GetDogRecommendationBody = z.infer<typeof GetDogRecommendationBodySchema>

export const GetDogRecommendationResponseSchema = z.object({
  dailyEnergyKilocalories: z.number().gt(0),
  puppyMealWeights: PuppyMealWeightsSchema.nullable(),
  weightMilestones: WeightMilestoneSchema.array().nullable(),
  averageAdultAgeMonths: z.number().int().positive().nullable(),
})
export type GetDogRecommendationResponse = z.infer<typeof GetDogRecommendationResponseSchema>

export const MigrateDogHistoryDataRequestSchema = z.object({
  dryRun: z.coerce.boolean().default(false),
  userId: z.string().uuid(),
  selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
  removalReasons: z.array(MappedAnimalRemovalReasonSchema),
  dogStatusHistory: AnimalStatusHistoryResponseSchema.array(),
})
export type MigrateDogHistoryDataRequest = z.infer<typeof MigrateDogHistoryDataRequestSchema>

export const MigrateDogHistoryOutputSchema = z.object({
  success: z.boolean(),
})

export type MigrateDogHistoryGeneralOutputResponse = z.infer<typeof MigrateDogHistoryOutputSchema>

export const DeleteMigratedDogHistoryDataRequestSchema = z.object({
  userId: z.string().uuid(),
  selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to delete. Defaults to all implemented tables.'),
})

export type DeleteMigratedDogHistoryDataRequest = z.infer<typeof DeleteMigratedDogHistoryDataRequestSchema>

export const DeleteMigratedDogHistoryDataResponseSchema = z.object({
  success: z.boolean(),
})

export type DeleteMigratedDogHistoryDataResponse = z.infer<typeof DeleteMigratedDogHistoryDataResponseSchema>

export const VerifyMigratedDogHistoryDataRequestSchema = z.object({
  userId: z.string().uuid(),
  selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to verify. Defaults to all implemented tables.'),
  removalReasons: z.array(MappedAnimalRemovalReasonSchema),
  dogStatusHistory: AnimalStatusHistoryResponseSchema.array(),
})
export type VerifyMigratedDogHistoryDataRequest = z.infer<typeof VerifyMigratedDogHistoryDataRequestSchema>

export const VerifyDogHistoryDataMismatchesSchema = z.object({
  path: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type VerifyDogHistoryDataMismatches = z.infer<typeof VerifyDogHistoryDataMismatchesSchema>

export const VerifyMigratedDogHistoryDataResponseSchema = z.object({
  userId: z.string().uuid(),
  success: z.boolean(),
  mismatches: VerifyDogHistoryDataMismatchesSchema.array(),
})

export type VerifyMigratedDogHistoryDataResponse = z.infer<typeof VerifyMigratedDogHistoryDataResponseSchema>

export const GenericMigrateFieldSchema = z.object({
  legacyId: z.number().nonnegative(),
  legacyName: z.string(),
})

export type GenericMigrateField = z.infer<typeof GenericMigrateFieldSchema>

export const MigrateDogSchema = z.object({
  id: z.string().uuid(),
  legacyId: z.number().int(),
  name: z.string().optional().nullable(),
  dateOfBirth: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD.')
    .nullable()
    .optional(),
  dateOfBirthIsApproximate: z.boolean(),
  dateOfDeath: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD.')
    .nullable()
    .optional(),
  dateOfDeathIsApproximate: z.boolean(),
  weight: z.string().nullable().optional(),
  gender: z.nativeEnum(DogGender).optional(),
  bodyType: z.nativeEnum(DogBodyType).optional(),
  activityLevel: z.nativeEnum(DogActivityLevel).optional(),
  eatingHabits: z.nativeEnum(DogEatingHabits).optional(),
  breeds: GenericMigrateFieldSchema.array(),
  healthIssues: GenericMigrateFieldSchema.array(),
  foodAllergies: GenericMigrateFieldSchema.array(),
  previousFoods: z.string().array(),
  previousFoodBrands: z.string().array(),
  lifestage: z.nativeEnum(DogLifestage).nullish(),
  ageAtSignupWeeks: z.number().int().nullable().optional()
})

export type MigrateDog = z.infer<typeof MigrateDogSchema>

export const DogImageToMigrateSchema = z.object({
  id: z.string().uuid(),
  legacyFilename: z.string(),
})

export type DogImageToMigrate = z.infer<typeof DogImageToMigrateSchema>

export const DogDeactivationReasonToMigrateSchema = z.object({
  deactivatedAt: z.string().datetime(),
  reasonLegacyId: z.number().int(),
  reasonDescription: z.string(),
  otherReason: z.string().nullable().optional(),
})

export type DogDeactivationReasonToMigrate = z.infer<typeof DogDeactivationReasonToMigrateSchema>

export const DogToMigrateSchema = z.object({
  id: z.string().uuid(),
  images: z.array(DogImageToMigrateSchema),
  deactivationReason: DogDeactivationReasonToMigrateSchema.nullable().optional(),
  createdDate: z.string().datetime().nullable().optional(),
  dog: MigrateDogSchema,
})

export type DogToMigrate = z.infer<typeof DogToMigrateSchema>

export const AnimalMigrationInputSchema = z.object({
  dryRun: z.boolean().optional(),
  userId: z.string().uuid(),
  legacyDogs: z.array(DogToMigrateSchema).min(1),
})

export type AnimalMigrationInput = z.infer<typeof AnimalMigrationInputSchema>

export const AnimalMigrationOutputSchema = z.object({
  userId: z.string().uuid(),
  migratedDogs: DogDtoSchema.array().min(1),
})

export type AnimalMigrationOutput = z.infer<typeof AnimalMigrationOutputSchema>

export const AnimalCleanupMigrationInputSchema = z.object({
  userId: z.string().uuid(),
})

export type AnimalCleanupMigrationInput = z.infer<typeof AnimalCleanupMigrationInputSchema>

export const AnimalCleanupMigrationOutputSchema = z.object({
  success: z.boolean(),
})

export type AnimalCleanupMigrationOutput = z.infer<typeof AnimalCleanupMigrationOutputSchema>

export const AnimalVerificationDogMismatchSchema = z.object({
  path: z.string(),
  expected: z.string(),
  actual: z.string(),
})

export type AnimalVerificationDogMismatch = z.infer<typeof AnimalVerificationDogMismatchSchema>

export const AnimalVerificationOutputSchema = z.object({
  success: z.boolean(),
  userId: z.string().uuid(),
  mismatches: z.array(AnimalVerificationDogMismatchSchema),
})

export type AnimalVerificationOutput = z.infer<typeof AnimalVerificationOutputSchema>

export const GetPuppiesForGraduationQuerySchema = z.object({
  cursor: z.string().uuid().optional().describe('Animal id from the previous page; returns puppies with id > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(100),
})

export type GetPuppiesForGraduationQuery = z.infer<typeof GetPuppiesForGraduationQuerySchema>

export const SlimPuppyDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  dateOfBirth: z.string().date(),
  lifestage: DogLifestageSchema,
  gender: DogGenderSchema,
  ageInWeeks: z.number().int().nonnegative(),
  adultAgeInWeeks: z.number().int().positive().nullable(),
  weight: z.number().nonnegative(),
  breedSize: DogBreedSizeClassification.nullable(),
  createdAt: z.string().datetime(), // dog.animal.createdAt
})

export type SlimPuppyDto = z.infer<typeof SlimPuppyDtoSchema>

export const GetPuppiesForGraduationResponseSchema = z.object({
  data: SlimPuppyDtoSchema.array(),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.string().uuid().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetPuppiesForGraduationResponse = z.infer<typeof GetPuppiesForGraduationResponseSchema>
